/** Client WebSocket: riconnessione automatica, playerId stabile, orologio
 * allineato al server (il timer del round e' del server, non del telefono). */

import type { InMsg, OutMsg } from "../../worker/src/protocol";

/** In produzione il Worker sta sullo stesso hostname del sito (route /api/*):
 * stessa origine, niente CORS. In sviluppo i due server sono separati. */
export const API: string =
  import.meta.env.VITE_API || (import.meta.env.DEV ? "http://localhost:8787" : location.origin);

function wsUrl(codice: string): string {
  const u = new URL(API);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws";
  u.search = `?codice=${codice}`;
  return u.toString();
}

export function playerId(): string {
  let id = localStorage.getItem("playerId");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("playerId", id);
  }
  return id;
}

/** Dietro Cloudflare Access una sessione scaduta non da' un errore: al posto
 * del JSON arriva la pagina di login. Va riconosciuta, altrimenti sembra un
 * server rotto. */
async function leggiJson(r: Response): Promise<any> {
  if (!(r.headers.get("content-type") ?? "").includes("application/json")) {
    throw new Error("ACCESS");
  }
  return r.json();
}

export async function creaPartita(): Promise<string> {
  const r = await fetch(API + "/api/partita", { method: "POST" });
  const d = await leggiJson(r);
  if (!r.ok) throw new Error("creazione fallita");
  return d.codice as string;
}

export async function esiste(codice: string): Promise<boolean> {
  const r = await fetch(`${API}/api/partita/${codice}`);
  return r.ok && (await leggiJson(r)).esiste === true;
}

export class Net {
  private ws: WebSocket | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  /** rientri falliti di fila: dopo un po' non e' piu' un problema di rete */
  tentativi = 0;
  private chiuso = false;
  /** ms da sommare a Date.now() per stare sull'orologio del server */
  scarto = 0;

  constructor(
    private codice: string,
    private nome: string,
    private host: boolean,
    private onMsg: (m: OutMsg) => void,
    private onStato: (connesso: boolean) => void,
  ) {}

  apri() {
    this.chiuso = false;
    const ws = new WebSocket(wsUrl(this.codice));
    this.ws = ws;
    ws.onopen = () => {
      this.tentativi = 0;
      this.onStato(true);
      this.manda({ t: "hello", playerId: playerId(), nome: this.nome, host: this.host });
      this.ping = setInterval(() => this.manda({ t: "ping" }), 25_000);
    };
    ws.onmessage = (ev) => {
      const m = JSON.parse(ev.data as string) as OutMsg;
      if (m.t === "domanda") this.scarto = m.d.adesso - Date.now();
      this.onMsg(m);
    };
    ws.onclose = () => {
      if (this.ping) clearInterval(this.ping);
      this.ping = null;
      this.onStato(false);
      if (this.chiuso) return;
      // rientro con lo stesso playerId: il DO ritrova punteggio e round
      const attesa = Math.min(8000, 400 * 2 ** this.tentativi++);
      this.timer = setTimeout(() => this.apri(), attesa);
    };
    ws.onerror = () => ws.close();
  }

  manda(m: InMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  chiudi() {
    this.chiuso = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close();
  }
}
